import { motion } from "framer-motion";
import { Phone, FileSearch, Rocket, HeartHandshake } from "lucide-react";

const steps = [
  { num: "01", icon: Phone, title: "Free Discovery Call", duration: "30 mins", desc: "Tell us your business challenge. We'll quickly assess if AI can help and which approach fits — no jargon, no pressure.", outcome: "You'll know if AI is right for you", color: "bg-[#2a9d8f]" },
  { num: "02", icon: FileSearch, title: "Audit & Roadmap", duration: "2-3 weeks", desc: "We dig into your data, processes, and systems. You get a prioritized roadmap with 3 concrete AI use cases ranked by ROI and feasibility.", outcome: "Clear plan with costs & timelines", color: "bg-[#c4683c]" },
  { num: "03", icon: Rocket, title: "Build & Deploy", duration: "4-16 weeks", desc: "We implement the solution — SaaS setup, custom AI tool, or analytics dashboard — integrated into your existing systems with your team trained.", outcome: "Working solution your team uses daily", color: "bg-[#d4a853]" },
  { num: "04", icon: HeartHandshake, title: "Measure & Scale", duration: "Ongoing", desc: "We track results against agreed KPIs. You pay based on outcomes. When it works, we scale it across your organization.", outcome: "Measurable ROI within 90 days", color: "bg-[#6b8e73]" },
];

export default function ThreePillars() {
  return (
    <section id="approach" data-testid="approach-section" className="py-24 md:py-32 relative bg-[#faf7f2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#2a9d8f] mb-4">How It Works</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>
            From First Call to <span className="gradient-text">Measurable ROI</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="text-base text-[#5c564e] mt-3 max-w-xl mx-auto">
            A straightforward 4-step process. No multi-year contracts. You see results before you commit to scale.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.num} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
              className="relative" data-testid={`pillar-card-${i}`}>
              <div className="warm-card p-6 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center`}>
                    <s.icon className="text-white w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-[#b8afa4]">STEP {s.num}</span>
                    <p className="text-xs text-[#8a8078]">{s.duration}</p>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-[#2d2926] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>{s.title}</h3>
                <p className="text-sm text-[#5c564e] leading-relaxed mb-4">{s.desc}</p>
                <div className="pt-3 border-t border-[#ebe5dc]">
                  <p className="text-xs text-[#2a9d8f] font-medium">{s.outcome}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
